import { useState } from 'react';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import { fmtAbv } from '../utils/format.js';
import { uiConfirm, uiAlert } from '../utils/dialogs.js';

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' }, warn: { background: '#d97706', color: '#fff' }, info: { background: '#2563eb', color: '#fff' } };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}
function Input(props) {
  return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}
function Select({ children, ...props }) {
  return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>;
}
function Textarea(props) {
  return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />;
}
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function BtnRow({ children }) { return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>; }

function ErrBanner({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p> : null; }

// ── Status badges ─────────────────────────────────────────────────────────────
const BADGE = {
  open:              { bg: '#fef9c3', color: '#854d0e' },
  investigating:     { bg: '#fff7ed', color: '#9a3412' },
  corrective_action: { bg: '#fef3c7', color: '#92400e' },
  closed:            { bg: '#dcfce7', color: '#166534' },
  pending:           { bg: '#f3f4f6', color: '#6b7280' },
  pending_review:    { bg: '#f3f4f6', color: '#6b7280' },
  approved:          { bg: '#dcfce7', color: '#166534' },
  conditional:       { bg: '#fef3c7', color: '#92400e' },
  suspended:         { bg: '#ffedd5', color: '#9a3412' },
  rejected:          { bg: '#fee2e2', color: '#991b1b' },
  review_due:        { bg: '#fef3c7', color: '#92400e' },
  low:               { bg: '#dcfce7', color: '#166534' },
  medium:            { bg: '#fef3c7', color: '#92400e' },
  high:              { bg: '#fee2e2', color: '#991b1b' },
  critical:          { bg: '#fce7f3', color: '#9d174d' },
  draft:             { bg: '#f3f4f6', color: '#6b7280' },
  active:            { bg: '#dcfce7', color: '#166534' },
  archived:          { bg: '#ede9fe', color: '#5b21b6' },
};

function Badge({ val, label }) {
  const c = BADGE[val] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{label ?? val?.replace(/_/g, ' ')}</span>;
}

function Table({ headers, children, empty = 'No records found.' }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            {headers.map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function TR({ children, onClick }) {
  return <tr onClick={onClick} style={{ borderTop: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default' }}
    onMouseEnter={onClick ? e => e.currentTarget.style.background = 'var(--surface-2)' : undefined}
    onMouseLeave={onClick ? e => e.currentTarget.style.background = 'transparent' : undefined}>{children}</tr>;
}
function TD({ children, style }) { return <td style={{ padding: '10px 14px', ...style }}>{children ?? '—'}</td>; }

function fmt(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '—'; }

// ── Overview / Dashboard ──────────────────────────────────────────────────────
function Overview() {
  const { data } = useApi('/salsa/dashboard');
  const s = data ?? {};
  const cards = [
    { label: 'Open NCs',           val: s.open_non_conformances ?? '–', c: '#dc2626' },
    { label: 'Open Complaints',    val: s.open_complaints       ?? '–', c: '#d97706' },
    { label: 'Suppliers Due Review', val: s.suppliers_due_review ?? '–', c: '#7c3aed' },
    { label: 'Expiring Training',  val: s.expiring_training_records ?? '–', c: '#2563eb' },
    { label: 'Active HACCP Plans', val: s.active_haccp_plans    ?? '–', c: '#059669' },
    { label: 'Overdue Maintenance',val: s.overdue_maintenance   ?? '–', c: '#dc2626' },
  ];
  return (
    <div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>Live compliance snapshot — select a tab to manage each area.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        {cards.map(({ label, val, c }) => (
          <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderLeft: `4px solid ${c}`, borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c }}>{val}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Non-Conformances ──────────────────────────────────────────────────────────
const NC_TYPES = ['ingredient', 'packaging', 'process', 'product', 'equipment', 'environmental', 'documentation', 'supplier', 'other'];
const NC_STATUSES = ['open', 'investigating', 'corrective_action', 'closed'];
const SEVERITIES  = ['low', 'medium', 'high', 'critical'];

function CreateNcModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ nc_type: '', severity: 'medium', description: '', immediate_action: '', detected_date: new Date().toISOString().slice(0,10), product_batch_ref: '', quantity_affected: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/salsa/non-conformances', { method: 'POST', body: form }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="New Non-Conformance" onClose={onClose}><form onSubmit={submit}><ErrBanner msg={err} />
      <Grid2>
        <Field label="Type *"><Select value={form.nc_type} onChange={set('nc_type')} required><option value="">Select...</option>{NC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</Select></Field>
        <Field label="Severity"><Select value={form.severity} onChange={set('severity')}>{SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}</Select></Field>
      </Grid2>
      <Grid2>
        <Field label="Detected date"><Input type="date" value={form.detected_date} onChange={set('detected_date')} /></Field>
        <Field label="Product / batch ref"><Input value={form.product_batch_ref} onChange={set('product_batch_ref')} placeholder="e.g. BATCH-2024-001" /></Field>
      </Grid2>
      <Field label="Description *"><Textarea rows={3} value={form.description} onChange={set('description')} required /></Field>
      <Field label="Immediate action taken"><Textarea rows={2} value={form.immediate_action} onChange={set('immediate_action')} /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create NC'}</Btn></BtnRow>
    </form></Modal>
  );
}

function UpdateNcStatusModal({ nc, onClose, onSaved }) {
  const nextStatuses = { open: ['investigating', 'closed'], investigating: ['corrective_action', 'closed'], corrective_action: ['closed'], closed: [] };
  const options = nextStatuses[nc.status] || [];
  const [form, setForm] = useState({ status: options[0] || nc.status, notes: '', root_cause: nc.root_cause || '', corrective_action: nc.corrective_action || '', immediate_action: nc.immediate_action || '', verification_notes: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch(`/salsa/non-conformances/${nc.id}/status`, { method: 'POST', body: form }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={`Update NC Status — ${nc.nc_ref}`} onClose={onClose} width={540}><form onSubmit={submit}><ErrBanner msg={err} />
      <Field label="New status"><Select value={form.status} onChange={set('status')}>{options.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}</Select></Field>
      <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
      {(form.status === 'corrective_action' || form.status === 'closed') && <>
        <Field label="Root cause"><Textarea rows={2} value={form.root_cause} onChange={set('root_cause')} /></Field>
        <Field label="Corrective action"><Textarea rows={2} value={form.corrective_action} onChange={set('corrective_action')} /></Field>
      </>}
      {form.status === 'closed' && <Field label="Verification notes"><Textarea rows={2} value={form.verification_notes} onChange={set('verification_notes')} /></Field>}
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Status'}</Btn></BtnRow>
    </form></Modal>
  );
}

function NonConformances() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const { data, loading, refetch } = useApi(`/salsa/non-conformances?status=${statusFilter}&limit=100`, [statusFilter]);
  const rows = data ?? [];
  return (
    <div>
      {showCreate && <CreateNcModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      {selected && <UpdateNcStatusModal nc={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); refetch(); }} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', ...NC_STATUSES].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s === 'all' ? 'All' : s.replace(/_/g,' ')}</button>
          ))}
        </div>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ New NC</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No records.</p> : (
        <Table headers={['Ref', 'Type', 'Severity', 'Description', 'Detected', 'Batch ref', 'Status', '']}>
          {rows.map(r => (
            <TR key={r.id}>
              <TD style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.nc_ref}</TD>
              <TD>{r.nc_type}</TD>
              <TD><Badge val={r.severity} /></TD>
              <TD style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</TD>
              <TD>{fmt(r.detected_date)}</TD>
              <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.product_batch_ref}</TD>
              <TD><Badge val={r.status} /></TD>
              <td style={{ padding: '8px 12px' }}>{r.status !== 'closed' && <Btn size="sm" variant="ghost" onClick={() => setSelected(r)}>Update</Btn>}</td>
            </TR>
          ))}
        </Table>
      )}
    </div>
  );
}

// ── Customer Complaints ───────────────────────────────────────────────────────
function CreateComplaintModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ customer_name: '', customer_email: '', product_name: '', batch_ref: '', severity: 'medium', complaint_type: '', description: '', complaint_date: new Date().toISOString().slice(0,10) });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/salsa/customer-complaints', { method: 'POST', body: form }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Log Customer Complaint" onClose={onClose}><form onSubmit={submit}><ErrBanner msg={err} />
      <Grid2>
        <Field label="Customer name"><Input value={form.customer_name} onChange={set('customer_name')} /></Field>
        <Field label="Customer email"><Input type="email" value={form.customer_email} onChange={set('customer_email')} /></Field>
      </Grid2>
      <Grid2>
        <Field label="Product"><Input value={form.product_name} onChange={set('product_name')} /></Field>
        <Field label="Batch ref"><Input value={form.batch_ref} onChange={set('batch_ref')} /></Field>
      </Grid2>
      <Grid2>
        <Field label="Type"><Input value={form.complaint_type} onChange={set('complaint_type')} placeholder="e.g. quality, labelling" /></Field>
        <Field label="Severity"><Select value={form.severity} onChange={set('severity')}>{SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}</Select></Field>
      </Grid2>
      <Field label="Complaint date"><Input type="date" value={form.complaint_date} onChange={set('complaint_date')} /></Field>
      <Field label="Description *"><Textarea rows={3} value={form.description} onChange={set('description')} required /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Log Complaint'}</Btn></BtnRow>
    </form></Modal>
  );
}

function Complaints() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [showCreate, setShowCreate] = useState(false);
  const { data, loading, refetch } = useApi(`/salsa/customer-complaints?status=${statusFilter}&limit=100`, [statusFilter]);
  const rows = data ?? [];
  return (
    <div>
      {showCreate && <CreateComplaintModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['open', 'investigating', 'closed', 'all'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s.replace(/_/g,' ')}</button>
          ))}
        </div>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ Log Complaint</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No complaints recorded.</p> : (
        <Table headers={['Ref', 'Customer', 'Product', 'Batch', 'Type', 'Severity', 'Date', 'Status']}>
          {rows.map(r => (
            <TR key={r.id}>
              <TD style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.complaint_ref}</TD>
              <TD>{r.customer_name}</TD>
              <TD>{r.product_name}</TD>
              <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.batch_ref}</TD>
              <TD>{r.complaint_type}</TD>
              <TD><Badge val={r.severity} /></TD>
              <TD>{fmt(r.complaint_date)}</TD>
              <TD><Badge val={r.status} /></TD>
            </TR>
          ))}
        </Table>
      )}
    </div>
  );
}

// ── Supplier Approvals ────────────────────────────────────────────────────────
// approval_status here must match the DB enum on supplier_approvals
// (migration 015_salsa_compliance.js) — 'pending'/'review_due' used to be
// offered here but aren't valid values and would fail on save.
const APPROVAL_STATUSES = ['pending_review', 'approved', 'conditional', 'suspended', 'rejected'];

export function SupplierApprovalModal({ initial, prefill, certificateUploadId, onClose, onSaved }) {
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? suppData ?? [];
  const blank = {
    supplier_id: '', approval_status: 'pending_review', risk_level: 'medium', risk_score: '',
    approval_date: new Date().toISOString().slice(0, 10), next_review_date: '', last_reviewed_at: '',
    has_food_safety_cert: false, cert_type: '', cert_expiry: '', cert_document_url: '', audit_notes: '',
  };
  const base = initial ? {
    ...blank, ...initial,
    approval_date: initial.approval_date?.slice(0, 10) ?? '',
    next_review_date: initial.next_review_date?.slice(0, 10) ?? '',
    last_reviewed_at: initial.last_reviewed_at?.slice(0, 10) ?? '',
    cert_expiry: initial.cert_expiry?.slice(0, 10) ?? '',
    risk_score: initial.risk_score ?? '',
    cert_type: initial.cert_type ?? '',
    audit_notes: initial.audit_notes ?? '',
  } : blank;
  // A parsed certificate's factual fields win over whatever was there
  // before (it's fresher, straight off the document just read) — judgment
  // calls like approval_status/risk_level are deliberately left alone for
  // a human to set.
  const withPrefill = prefill ? {
    ...base,
    supplier_id: prefill.supplier_id || base.supplier_id,
    cert_type: prefill.cert_type || base.cert_type,
    cert_expiry: prefill.expiry_date || base.cert_expiry,
    next_review_date: prefill.expiry_date || base.next_review_date,
    cert_document_url: prefill.cert_document_url || base.cert_document_url,
    has_food_safety_cert: prefill.cert_type ? true : base.has_food_safety_cert,
    last_reviewed_at: new Date().toISOString().slice(0, 10),
    audit_notes: [base.audit_notes, prefill.audit_note].filter(Boolean).join('\n\n'),
  } : base;
  const [form, setForm] = useState(withPrefill);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function viewCertFile(path) {
    try {
      const res = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(`Failed to load file (HTTP ${res.status})`);
      const blob = await res.blob();
      const win = window.open(URL.createObjectURL(blob), '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to view certificate files.');
    } catch (e) { alert(e.message); }
  }
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    // Only send real columns on this table — `form` also carries joined
    // display fields (supplier_name, approved_by_name, id...) from
    // `initial`, which the database would reject.
    const body = {
      supplier_id: form.supplier_id,
      approval_status: form.approval_status,
      risk_level: form.risk_level,
      risk_score: form.risk_score === '' ? null : parseInt(form.risk_score, 10),
      approval_date: form.approval_date || null,
      next_review_date: form.next_review_date || null,
      last_reviewed_at: form.last_reviewed_at || null,
      has_food_safety_cert: !!form.has_food_safety_cert,
      cert_type: form.cert_type || null,
      cert_expiry: form.cert_expiry || null,
      cert_document_url: form.cert_document_url || null,
      audit_notes: form.audit_notes || null,
    };
    try {
      if (initial?.id) await apiFetch(`/salsa/supplier-approvals/${initial.id}`, { method: 'PATCH', body });
      else await apiFetch('/salsa/supplier-approvals', { method: 'POST', body });
      if (certificateUploadId && form.supplier_id) {
        // Best-effort — the approval itself is already saved either way,
        // this only keeps the certificate's own status accurate.
        apiFetch(`/salsa/suppliers/${form.supplier_id}/certificates/${certificateUploadId}/mark-applied`, { method: 'POST' }).catch(() => {});
      }
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={initial?.id ? 'Edit Supplier Approval' : 'Add Supplier Approval'} onClose={onClose} width={560}><form onSubmit={submit}><ErrBanner msg={err} />
      {prefill?.flags?.length > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 12.5, marginBottom: 14 }}>
          {prefill.flags.map((f, i) => <div key={i}>⚠ {f}</div>)}
        </div>
      )}
      {form.cert_document_url && (
        <p style={{ margin: '0 0 14px', fontSize: 12.5 }}>
          <a href={form.cert_document_url.startsWith('http') ? form.cert_document_url : undefined} onClick={form.cert_document_url.startsWith('http') ? undefined : (e) => { e.preventDefault(); viewCertFile(form.cert_document_url); }} style={{ color: 'var(--pld-green)' }}>📄 View certificate file</a>
        </p>
      )}
      <Field label="Supplier *"><Select value={form.supplier_id} onChange={set('supplier_id')} required disabled={!!initial?.id}><option value="">Select supplier...</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
      <Grid2>
        <Field label="Status"><Select value={form.approval_status} onChange={set('approval_status')}>{APPROVAL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}</Select></Field>
        <Field label="Risk level"><Select value={form.risk_level} onChange={set('risk_level')}>{SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}</Select></Field>
      </Grid2>
      <Grid2>
        <Field label="Risk score (1-10)"><Input type="number" min="1" max="10" value={form.risk_score} onChange={set('risk_score')} placeholder="Optional" /></Field>
        <Field label="Last reviewed"><Input type="date" value={form.last_reviewed_at} onChange={set('last_reviewed_at')} /></Field>
      </Grid2>
      <Grid2>
        <Field label="Approval date"><Input type="date" value={form.approval_date} onChange={set('approval_date')} /></Field>
        <Field label="Next review date"><Input type="date" value={form.next_review_date} onChange={set('next_review_date')} /></Field>
      </Grid2>
      <Grid2>
        <Field label="Assurance / Certification"><Input value={form.cert_type} onChange={set('cert_type')} placeholder="e.g. BRCGS Food" /></Field>
        <Field label="Certificate expiry"><Input type="date" value={form.cert_expiry} onChange={set('cert_expiry')} /></Field>
      </Grid2>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '4px 0 12px' }}>
        <input type="checkbox" checked={!!form.has_food_safety_cert} onChange={set('has_food_safety_cert')} /> Holds a food safety certification
      </label>
      <Field label="Audit notes"><Textarea rows={2} value={form.audit_notes} onChange={set('audit_notes')} /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn></BtnRow>
    </form></Modal>
  );
}

// ── Upload Certificate ────────────────────────────────────────────────────────
// Reads a supplier's assurance/accreditation certificate and hands the
// extraction to SupplierApprovalModal as a prefill — nothing is saved here.
export function UploadCertificateModal({ onClose, onParsed, defaultSupplierId = '' }) {
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? suppData ?? [];
  const [supplierId, setSupplierId] = useState(defaultSupplierId);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!supplierId || !file) { setError('Choose a supplier and a file.'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/salsa/suppliers/${supplierId}/certificates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Upload failed (HTTP ${res.status})`);
      const parsed = typeof body.data.parsed_json === 'string' ? JSON.parse(body.data.parsed_json) : body.data.parsed_json;
      onParsed({ supplierId, certificateId: body.data.id, fileName: file.name, parsed });
    } catch (err) { setError(err.message); setUploading(false); }
  }

  return (
    <Modal title="Upload Certificate" onClose={onClose} width={480}>
      <form onSubmit={handleSubmit}>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          Upload a supplier's SALSA/BRCGS/organic/insurance certificate (PDF or photo). Blake reads it and pre-fills the Supplier Approval form below for you to check over before saving.
        </p>
        <Field label="Supplier *">
          <Select value={supplierId} onChange={e => setSupplierId(e.target.value)} required>
            <option value="">Select supplier...</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Certificate File (PDF or image) *">
          <input type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} required />
        </Field>
        <ErrBanner msg={error} />
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={uploading || !supplierId || !file}>{uploading ? 'Reading document…' : 'Read Document'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

export function buildCertAuditNote(parsed, fileName) {
  const bits = [];
  if (parsed.certificate_number) bits.push(`Cert. no. ${parsed.certificate_number}`);
  if (parsed.issuing_body) bits.push(`issued by ${parsed.issuing_body}`);
  if (parsed.issue_date) bits.push(`issued ${parsed.issue_date}`);
  if (parsed.scope) bits.push(`scope: ${parsed.scope}`);
  const detail = bits.length ? ` (${bits.join(', ')})` : '';
  return `Read from uploaded certificate "${fileName}"${detail}.`;
}

function SupplierApprovals() {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certPrefill, setCertPrefill] = useState(null); // { initial, prefill, certificateUploadId }
  const { data, loading, refetch } = useApi('/salsa/supplier-approvals');
  const rows = data ?? [];
  const today = new Date();

  function handleCertParsed({ supplierId, certificateId, fileName, parsed }) {
    setUploadingCert(false);
    const existing = rows.find(r => r.supplier_id === supplierId) || null;
    setCertPrefill({
      initial: existing,
      prefill: {
        supplier_id: supplierId,
        cert_type: parsed.cert_type,
        expiry_date: parsed.expiry_date,
        cert_document_url: `/salsa/suppliers/${supplierId}/certificates/${certificateId}/file`,
        audit_note: buildCertAuditNote(parsed, fileName),
        flags: parsed.name_matches_supplier === false
          ? [...(parsed.flags || []), `Certificate holder name may not match this supplier: "${parsed.certificate_holder_name || 'unknown'}"`]
          : (parsed.flags || []),
      },
      certificateUploadId: certificateId,
    });
  }

  return (
    <div>
      {showCreate && <SupplierApprovalModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      {editing && <SupplierApprovalModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
      {uploadingCert && <UploadCertificateModal onClose={() => setUploadingCert(false)} onParsed={handleCertParsed} />}
      {certPrefill && (
        <SupplierApprovalModal
          initial={certPrefill.initial}
          prefill={certPrefill.prefill}
          certificateUploadId={certPrefill.certificateUploadId}
          onClose={() => setCertPrefill(null)}
          onSaved={() => { setCertPrefill(null); refetch(); }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <Btn size="sm" variant="ghost" onClick={() => setUploadingCert(true)}>Upload Certificate</Btn>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ Add Approval</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No supplier approvals.</p> : (
        <Table headers={['Supplier', 'Status', 'Risk', 'Certification', 'Cert. Expiry', 'Last Reviewed', 'Next Review', '']}>
          {rows.map(r => {
            const overdue = r.next_review_date && new Date(r.next_review_date) < today;
            const certOverdue = r.cert_expiry && new Date(r.cert_expiry) < today;
            return (
              <TR key={r.id}>
                <TD style={{ fontWeight: 600 }}>{r.supplier_name}</TD>
                <TD><Badge val={r.approval_status} /></TD>
                <TD>{r.risk_level ? <Badge val={r.risk_level} label={r.risk_score ? `${r.risk_score} - ${r.risk_level}` : r.risk_level} /> : '—'}</TD>
                <TD style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cert_type || (r.has_food_safety_cert ? 'Yes' : '—')}</TD>
                <td style={{ padding: '10px 14px', color: certOverdue ? '#dc2626' : 'var(--text-muted)', fontWeight: certOverdue ? 700 : 400 }}>{fmt(r.cert_expiry)}{certOverdue && ' ⚠'}</td>
                <TD>{fmt(r.last_reviewed_at)}</TD>
                <td style={{ padding: '10px 14px', color: overdue ? '#dc2626' : 'var(--text-muted)', fontWeight: overdue ? 700 : 400 }}>{fmt(r.next_review_date)}{overdue && ' ⚠'}</td>
                <TD><Btn size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Btn></TD>
              </TR>
            );
          })}
        </Table>
      )}
    </div>
  );
}

// ── Approved Materials ────────────────────────────────────────────────────────
// Which supplier is approved to provide which specific branded raw
// material — a standard BRCGS control, distinct from approving the
// supplier itself (SupplierApprovals above) or PLD's own internal stock
// item catalogue.
const MATERIAL_STATUSES = ['approved', 'pending', 'rejected'];

function ApprovedMaterialModal({ initial, onClose, onSaved }) {
  const { data: suppData } = useApi('/suppliers');
  const suppliers = suppData?.suppliers ?? suppData ?? [];
  const blank = { supplier_id: '', brand_name: '', material_description: '', category: '', status: 'pending', notes: '' };
  const [form, setForm] = useState(initial ? { ...blank, ...initial, notes: initial.notes ?? '' } : blank);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    // Only send real columns on this table — `form` also carries joined
    // display fields (supplier_name, id, created_at...) from `initial`,
    // which the database would reject.
    const body = {
      supplier_id: form.supplier_id,
      brand_name: form.brand_name,
      material_description: form.material_description || null,
      category: form.category || null,
      status: form.status,
      notes: form.notes || null,
    };
    try {
      if (initial?.id) await apiFetch(`/salsa/approved-materials/${initial.id}`, { method: 'PATCH', body });
      else await apiFetch('/salsa/approved-materials', { method: 'POST', body });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title={initial?.id ? 'Review Approved Material' : 'Add Approved Material'} onClose={onClose}><form onSubmit={submit}><ErrBanner msg={err} />
      <Field label="Supplier *"><Select value={form.supplier_id} onChange={set('supplier_id')} required disabled={!!initial?.id}><option value="">Select supplier...</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
      <Field label="Brand / Product Name *"><Input value={form.brand_name} onChange={set('brand_name')} required placeholder="e.g. SafSpirit™ HG-1" /></Field>
      <Grid2>
        <Field label="What it is"><Input value={form.material_description ?? ''} onChange={set('material_description')} placeholder="e.g. Dry Yeast" /></Field>
        <Field label="Category"><Input value={form.category ?? ''} onChange={set('category')} placeholder="e.g. Food Additive" /></Field>
      </Grid2>
      <Field label="Status"><Select value={form.status} onChange={set('status')}>{MATERIAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</Select></Field>
      <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn></BtnRow>
    </form></Modal>
  );
}

function ApprovedMaterials() {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const { data, loading, refetch } = useApi('/salsa/approved-materials');
  const rows = (data ?? []).filter(r =>
    !search
    || r.brand_name?.toLowerCase().includes(search.toLowerCase())
    || r.supplier_name?.toLowerCase().includes(search.toLowerCase())
    || r.material_description?.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div>
      {showCreate && <ApprovedMaterialModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      {editing && <ApprovedMaterialModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>Which supplier is approved to provide which specific branded raw material. Click a row to review or change its status.</p>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ Add Material</Btn>
      </div>
      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brand, supplier or material..." style={{ marginBottom: 14, maxWidth: 340 }} />
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No approved materials recorded.</p> : (
        <Table headers={['Brand / Product', 'Material', 'Category', 'Supplier', 'Status', 'Notes', '']}>
          {rows.map(r => (
            <TR key={r.id} onClick={() => setEditing(r)}>
              <TD style={{ fontWeight: 600 }}>{r.brand_name}</TD>
              <TD>{r.material_description}</TD>
              <TD>{r.category}</TD>
              <TD>{r.supplier_name}</TD>
              <TD><Badge val={r.status} /></TD>
              <TD style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes}</TD>
              <TD><Btn size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(r); }}>Review</Btn></TD>
            </TR>
          ))}
        </Table>
      )}
    </div>
  );
}

// ── Training Records ──────────────────────────────────────────────────────────
function CreateTrainingModal({ onClose, onSaved }) {
  const { data: usersData } = useApi('/settings/users');
  const users = usersData?.users ?? usersData ?? [];
  const [form, setForm] = useState({ user_id: '', training_type: '', training_name: '', provider: '', completed_at: new Date().toISOString().slice(0,10), expires_at: '', certificate_ref: '', notes: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/salsa/training-records', { method: 'POST', body: { ...form, expires_at: form.expires_at || undefined } }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Add Training Record" onClose={onClose}><form onSubmit={submit}><ErrBanner msg={err} />
      <Field label="Team member *"><Select value={form.user_id} onChange={set('user_id')} required><option value="">Select...</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></Field>
      <Grid2>
        <Field label="Training type *"><Input value={form.training_type} onChange={set('training_type')} placeholder="e.g. food_hygiene" required /></Field>
        <Field label="Training name *"><Input value={form.training_name} onChange={set('training_name')} placeholder="e.g. Food Safety Level 2" required /></Field>
      </Grid2>
      <Grid2>
        <Field label="Provider"><Input value={form.provider} onChange={set('provider')} /></Field>
        <Field label="Certificate ref"><Input value={form.certificate_ref} onChange={set('certificate_ref')} /></Field>
      </Grid2>
      <Grid2>
        <Field label="Completed date"><Input type="date" value={form.completed_at} onChange={set('completed_at')} /></Field>
        <Field label="Expiry date"><Input type="date" value={form.expires_at} onChange={set('expires_at')} /></Field>
      </Grid2>
      <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add Record'}</Btn></BtnRow>
    </form></Modal>
  );
}

// The two-page induction temporary staff read and sign on their first day.
async function downloadInductionForm() {
  try {
    const res = await fetch('/api/salsa/induction-form', { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error(`Couldn't download the induction form (HTTP ${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = 'PLD Induction - temporary staff.docx'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { uiAlert(e.message); }
}

function TrainingRecords() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, loading, refetch } = useApi('/salsa/training-records');
  const rows = data ?? [];
  const today = new Date();
  const soon = new Date(today); soon.setDate(today.getDate() + 60);
  return (
    <div>
      {showCreate && <CreateTrainingModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <Btn size="sm" variant="ghost" onClick={downloadInductionForm}>⬇ Temp staff induction form</Btn>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ Add Training</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No training records.</p> : (
        <Table headers={['Team member', 'Type', 'Name', 'Provider', 'Completed', 'Expires', 'Cert ref']}>
          {rows.map(r => {
            const exp = r.expires_at ? new Date(r.expires_at) : null;
            const expired = exp && exp < today;
            const expiringSoon = exp && !expired && exp < soon;
            const expColor = expired ? '#dc2626' : expiringSoon ? '#d97706' : 'var(--text-muted)';
            return (
              <TR key={r.id}>
                <TD style={{ fontWeight: 600 }}>{r.user_name}</TD>
                <TD style={{ fontSize: 12 }}>{r.training_type}</TD>
                <TD>{r.training_name}</TD>
                <TD style={{ color: 'var(--text-muted)' }}>{r.provider}</TD>
                <TD>{fmt(r.completed_at)}</TD>
                <td style={{ padding: '10px 14px', color: expColor, fontWeight: expired || expiringSoon ? 700 : 400 }}>{fmt(r.expires_at)}{expired && ' ✗'}{expiringSoon && ' ⚠'}</td>
                <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.certificate_ref}</TD>
              </TR>
            );
          })}
        </Table>
      )}
    </div>
  );
}

// ── Allergens ─────────────────────────────────────────────────────────────────
function CreateAllergenModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', regulatory_ref: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/salsa/allergens', { method: 'POST', body: form }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Add Allergen" onClose={onClose} width={420}><form onSubmit={submit}><ErrBanner msg={err} />
      <Field label="Allergen name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Gluten" /></Field>
      <Field label="Regulatory reference"><Input value={form.regulatory_ref} onChange={e => setForm(f => ({ ...f, regulatory_ref: e.target.value }))} placeholder="e.g. Annex II Reg 1169/2011" /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add'}</Btn></BtnRow>
    </form></Modal>
  );
}

function Allergens() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, loading, refetch } = useApi('/salsa/allergens');
  const rows = data ?? [];
  return (
    <div>
      {showCreate && <CreateAllergenModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><Btn size="sm" onClick={() => setShowCreate(true)}>+ Add Allergen</Btn></div>
      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No allergens in register.</p> : (
        <Table headers={['Allergen', 'Regulatory reference']}>
          {rows.map(r => <TR key={r.id}><TD style={{ fontWeight: 600 }}>{r.name}</TD><TD style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.regulatory_ref}</TD></TR>)}
        </Table>
      )}
    </div>
  );
}

// ── HACCP Plans ───────────────────────────────────────────────────────────────
function CreateHaccpModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', version: '1.0', process_description: '', status: 'draft' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/salsa/haccp-plans', { method: 'POST', body: form }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="New HACCP Plan" onClose={onClose}><form onSubmit={submit}><ErrBanner msg={err} />
      <Field label="Title *"><Input value={form.title} onChange={set('title')} required /></Field>
      <Grid2>
        <Field label="Version"><Input value={form.version} onChange={set('version')} /></Field>
        <Field label="Status"><Select value={form.status} onChange={set('status')}><option value="draft">Draft</option><option value="active">Active</option></Select></Field>
      </Grid2>
      <Field label="Process description"><Textarea rows={3} value={form.process_description} onChange={set('process_description')} /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Plan'}</Btn></BtnRow>
    </form></Modal>
  );
}

function HaccpPlans() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, loading, refetch } = useApi('/salsa/haccp-plans');
  const rows = data ?? [];
  return (
    <div>
      {showCreate && <CreateHaccpModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><Btn size="sm" onClick={() => setShowCreate(true)}>+ New HACCP Plan</Btn></div>
      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No HACCP plans created yet.</p> : (
        <Table headers={['Ref', 'Title', 'Version', 'Status', 'Lead', 'CCPs', 'Created']}>
          {rows.map(r => (
            <TR key={r.id}>
              <TD style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.plan_ref}</TD>
              <TD style={{ fontWeight: 600 }}>{r.title}</TD>
              <TD style={{ color: 'var(--text-muted)' }}>{r.version}</TD>
              <TD><Badge val={r.status} /></TD>
              <TD style={{ color: 'var(--text-muted)' }}>{r.lead_person_name}</TD>
              <TD style={{ color: 'var(--text-muted)' }}>{r.ccp_count ?? '—'}</TD>
              <TD>{fmt(r.created_at)}</TD>
            </TR>
          ))}
        </Table>
      )}
    </div>
  );
}

// ── Food Fraud Assessments ────────────────────────────────────────────────────
function CreateFoodFraudModal({ onClose, onSaved }) {
  const { data: stockData } = useApi('/stock');
  const items = stockData?.stock_items ?? stockData ?? [];
  const [form, setForm] = useState({ stock_item_id: '', risk_rating: 'medium', vulnerability_assessment: '', control_measures: '', next_review_date: '' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/salsa/food-fraud-assessments', { method: 'POST', body: { ...form, next_review_date: form.next_review_date || undefined } }); onSaved(); }
    catch(e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="New Food Fraud Assessment" onClose={onClose}><form onSubmit={submit}><ErrBanner msg={err} />
      <Field label="Stock item *"><Select value={form.stock_item_id} onChange={set('stock_item_id')} required><option value="">Select...</option>{items.map(s => <option key={s.id} value={s.id}>{s.name} ({s.sku})</option>)}</Select></Field>
      <Grid2>
        <Field label="Risk rating"><Select value={form.risk_rating} onChange={set('risk_rating')}>{SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}</Select></Field>
        <Field label="Next review date"><Input type="date" value={form.next_review_date} onChange={set('next_review_date')} /></Field>
      </Grid2>
      <Field label="Vulnerability assessment"><Textarea rows={3} value={form.vulnerability_assessment} onChange={set('vulnerability_assessment')} /></Field>
      <Field label="Control measures"><Textarea rows={2} value={form.control_measures} onChange={set('control_measures')} /></Field>
      <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Assessment'}</Btn></BtnRow>
    </form></Modal>
  );
}

function FoodFraud() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, loading, refetch } = useApi('/salsa/food-fraud-assessments');
  const rows = data ?? [];
  const today = new Date();
  return (
    <div>
      {showCreate && <CreateFoodFraudModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><Btn size="sm" onClick={() => setShowCreate(true)}>+ New Assessment</Btn></div>
      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No food fraud assessments.</p> : (
        <Table headers={['Item', 'SKU', 'Risk', 'Assessed by', 'Assessed', 'Next review']}>
          {rows.map(r => {
            const overdue = r.next_review_date && new Date(r.next_review_date) < today;
            return (
              <TR key={r.id}>
                <TD style={{ fontWeight: 600 }}>{r.stock_item_name}</TD>
                <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.sku}</TD>
                <TD><Badge val={r.risk_rating} /></TD>
                <TD style={{ color: 'var(--text-muted)' }}>{r.assessed_by_name}</TD>
                <TD>{fmt(r.assessed_at ?? r.created_at)}</TD>
                <td style={{ padding: '10px 14px', color: overdue ? '#dc2626' : 'var(--text-muted)', fontWeight: overdue ? 700 : 400 }}>{fmt(r.next_review_date)}{overdue && ' ⚠'}</td>
              </TR>
            );
          })}
        </Table>
      )}
    </div>
  );
}

// ── Maintenance ───────────────────────────────────────────────────────────────
const FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'quarterly', 'biannually', 'annually', 'as_required'];

function humanizeFrequency(f) {
  if (!f) return '—';
  return f.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
}

function AddScheduleModal({ equipment, onClose, onSaved }) {
  const [form, setForm] = useState({
    equipment_id: '', maintenance_type: '', frequency: 'monthly', next_due_at: '',
    responsible_party: '', contractor: '', calibration_required: false, procedure_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.equipment_id) return setErr('Equipment is required');
    if (!form.maintenance_type.trim()) return setErr('Maintenance type is required');
    setErr('');
    setSaving(true);
    try {
      await apiFetch('/salsa/maintenance-schedules', {
        method: 'POST',
        body: { ...form, next_due_at: form.next_due_at || null },
      });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add Maintenance Schedule" onClose={onClose} width={560}>
      <ErrBanner msg={err} />
      <Field label="Equipment *">
        <Select value={form.equipment_id} onChange={e => set('equipment_id', e.target.value)}>
          <option value="">Select equipment…</option>
          {equipment.map(eq => <option key={eq.id} value={eq.id}>{eq.name} ({eq.code})</option>)}
        </Select>
      </Field>
      <Field label="Maintenance Type *">
        <Input value={form.maintenance_type} onChange={e => set('maintenance_type', e.target.value)} placeholder="e.g. Filter change, Calibration, Descale" />
      </Field>
      <Grid2>
        <Field label="Frequency">
          <Select value={form.frequency} onChange={e => set('frequency', e.target.value)}>
            {FREQUENCY_OPTIONS.map(f => <option key={f} value={f}>{humanizeFrequency(f)}</option>)}
          </Select>
        </Field>
        <Field label="Next Due">
          <Input type="date" value={form.next_due_at} onChange={e => set('next_due_at', e.target.value)} />
        </Field>
      </Grid2>
      <Grid2>
        <Field label="Responsible Party">
          <Input value={form.responsible_party} onChange={e => set('responsible_party', e.target.value)} />
        </Field>
        <Field label="Contractor (if external)">
          <Input value={form.contractor} onChange={e => set('contractor', e.target.value)} />
        </Field>
      </Grid2>
      <Field label="Calibration Required?">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.calibration_required} onChange={e => set('calibration_required', e.target.checked)} />
          Yes
        </label>
      </Field>
      <Field label="Procedure Notes">
        <Textarea rows={3} value={form.procedure_notes} onChange={e => set('procedure_notes', e.target.value)} />
      </Field>
      <BtnRow>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add Schedule'}</Btn>
      </BtnRow>
    </Modal>
  );
}

function LogMaintenanceModal({ equipment, schedules, presetScheduleId, onClose, onSaved }) {
  const preset = presetScheduleId ? schedules.find(s => s.id === presetScheduleId) : null;
  const [form, setForm] = useState({
    schedule_id: presetScheduleId || '',
    equipment_id: preset?.equipment_id || '',
    carried_out_at: new Date().toISOString().slice(0, 10),
    carried_out_by: '',
    work_description: '',
    product_safety_checked: false,
    calibration_result: 'not_applicable',
    follow_up_required: false,
    follow_up_notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function onScheduleChange(id) {
    const s = schedules.find(x => x.id === id);
    setForm(f => ({ ...f, schedule_id: id, equipment_id: s ? s.equipment_id : f.equipment_id }));
  }

  async function save() {
    if (!form.equipment_id) return setErr('Equipment is required');
    if (!form.carried_out_by.trim()) return setErr('Carried out by is required');
    if (!form.work_description.trim()) return setErr('Work description is required');
    setErr('');
    setSaving(true);
    try {
      await apiFetch('/salsa/maintenance-records', {
        method: 'POST',
        body: { ...form, schedule_id: form.schedule_id || null },
      });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Log Maintenance" onClose={onClose} width={560}>
      <ErrBanner msg={err} />
      <Field label="Linked Schedule (optional)">
        <Select value={form.schedule_id} onChange={e => onScheduleChange(e.target.value)}>
          <option value="">None — one-off / ad hoc</option>
          {schedules.map(s => <option key={s.id} value={s.id}>{s.equipment_name} — {s.maintenance_type}</option>)}
        </Select>
      </Field>
      <Field label="Equipment *">
        <Select value={form.equipment_id} onChange={e => set('equipment_id', e.target.value)}>
          <option value="">Select equipment…</option>
          {equipment.map(eq => <option key={eq.id} value={eq.id}>{eq.name} ({eq.code})</option>)}
        </Select>
      </Field>
      <Grid2>
        <Field label="Carried Out On">
          <Input type="date" value={form.carried_out_at} onChange={e => set('carried_out_at', e.target.value)} />
        </Field>
        <Field label="Carried Out By *">
          <Input value={form.carried_out_by} onChange={e => set('carried_out_by', e.target.value)} placeholder="Name" />
        </Field>
      </Grid2>
      <Field label="Work Description *">
        <Textarea rows={3} value={form.work_description} onChange={e => set('work_description', e.target.value)} />
      </Field>
      <Field label="Product Safety Checked?">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.product_safety_checked} onChange={e => set('product_safety_checked', e.target.checked)} />
          Yes — confirmed no risk to product safety
        </label>
      </Field>
      <Field label="Calibration Result">
        <Select value={form.calibration_result} onChange={e => set('calibration_result', e.target.value)}>
          <option value="not_applicable">Not applicable</option>
          <option value="pass">Pass</option>
          <option value="fail">Fail</option>
        </Select>
      </Field>
      <Field label="Follow-up Required?">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.follow_up_required} onChange={e => set('follow_up_required', e.target.checked)} />
          Yes
        </label>
      </Field>
      {form.follow_up_required && (
        <Field label="Follow-up Notes">
          <Textarea rows={2} value={form.follow_up_notes} onChange={e => set('follow_up_notes', e.target.value)} />
        </Field>
      )}
      <BtnRow>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Log Maintenance'}</Btn>
      </BtnRow>
    </Modal>
  );
}

function Maintenance() {
  const { data, loading, refetch } = useApi('/salsa/maintenance-schedules');
  const { data: equipmentData } = useApi('/production/equipment');
  const rows = data ?? [];
  // /production/equipment responds with { equipment: [...] }, not { data: [...] } —
  // useApi falls back to returning the whole raw JSON body in that case.
  const equipment = Array.isArray(equipmentData) ? equipmentData : (equipmentData?.equipment ?? []);
  const [modal, setModal] = useState(null); // null | 'add-schedule' | { log: scheduleId|null }
  const today = new Date();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Maintenance Schedules</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" size="sm" onClick={() => setModal({ log: null })}>Log Maintenance</Btn>
          <Btn size="sm" onClick={() => setModal('add-schedule')}>+ Add Schedule</Btn>
        </div>
      </div>

      {!loading && equipment.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
          No equipment found — add equipment on the Production page before creating a maintenance schedule.
        </p>
      )}

      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading...</p> : rows.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No schedules.</p> : (
        <Table headers={['Equipment', 'Code', 'Type', 'Frequency', 'Last completed', 'Next due', '']}>
          {rows.map(r => {
            const overdue = r.next_due_at && new Date(r.next_due_at) < today;
            return (
              <TR key={r.id}>
                <TD style={{ fontWeight: 600 }}>{r.equipment_name}</TD>
                <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.equipment_code}</TD>
                <TD>{r.maintenance_type}</TD>
                <TD style={{ color: 'var(--text-muted)' }}>{humanizeFrequency(r.frequency)}</TD>
                <TD>{fmt(r.last_completed_at)}</TD>
                <td style={{ padding: '10px 14px', color: overdue ? '#dc2626' : 'var(--text-muted)', fontWeight: overdue ? 700 : 400 }}>{fmt(r.next_due_at)}{overdue && ' ⚠ Overdue'}</td>
                <TD><Btn variant="ghost" size="sm" onClick={() => setModal({ log: r.id })}>Log</Btn></TD>
              </TR>
            );
          })}
        </Table>
      )}

      {modal === 'add-schedule' && (
        <AddScheduleModal equipment={equipment} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />
      )}
      {modal && typeof modal === 'object' && 'log' in modal && (
        <LogMaintenanceModal equipment={equipment} schedules={rows} presetScheduleId={modal.log} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />
      )}
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

// ── COSHH ─────────────────────────────────────────────────────────────────────
const HAZARD_COLOURS = {
  'Highly Flammable': '#fff3cd',
  'Flammable':        '#fff3cd',
  'Corrosive':        '#f8d7da',
  'Oxidising':        '#fde8d8',
  'Asphyxiant':       '#d1ecf1',
  'Irritant':         '#e2e3e5',
  'Sensitiser':       '#e8d5f5',
  'Toxic':            '#f8d7da',
};

function hazardColour(classification) {
  if (!classification) return '#f5f5f5';
  for (const [key, col] of Object.entries(HAZARD_COLOURS)) {
    if (classification.toLowerCase().includes(key.toLowerCase())) return col;
  }
  return '#f5f5f5';
}

function reviewStatus(reviewDue) {
  if (!reviewDue) return null;
  const days = Math.ceil((new Date(reviewDue) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0)   return { label: 'Overdue',    colour: 'var(--status-critical)' };
  if (days <= 30) return { label: `Due in ${days}d`, colour: 'var(--status-major)' };
  if (days <= 90) return { label: `Due in ${days}d`, colour: '#e67e22' };
  return null;
}

function CoshhModal({ substance, onClose, onSaved }) {
  const isEdit = !!substance?.id;
  const [form, setForm] = useState(substance ? { ...substance } : {
    substance_name:'', hazard_classification:'', ghs_symbols:'', sds_held: false, sds_date:'',
    where_used:'', storage_location:'', approx_qty_on_site:'', who_at_risk:'',
    exposure_routes:'', control_measures:'', ppe_required:'', first_aid:'',
    emergency_action:'', responsible_person:'', date_assessed:'', review_due:'', notes:'',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.substance_name.trim()) return alert('Substance name required');
    setSaving(true);
    try {
      if (isEdit) {
        await apiFetch(`/coshh/${substance.id}`, { method: 'PUT', body: form });
      } else {
        await apiFetch('/coshh', { method: 'POST', body: form });
      }
      onSaved();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  const field = (label, key, opts = {}) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:3 }}>{label}</label>
      {opts.textarea ? (
        <textarea rows={opts.rows || 3} value={form[key] || ''} onChange={e => set(key, e.target.value)}
          style={{ width:'100%', padding:'6px 8px', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }} />
      ) : opts.checkbox ? (
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
          <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} />
          Yes
        </label>
      ) : (
        <input type={opts.type || 'text'} value={form[key] || ''} onChange={e => set(key, e.target.value)}
          style={{ width:'100%', padding:'6px 8px', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', fontSize:13, boxSizing:'border-box' }} />
      )}
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:40, overflowY:'auto' }}>
      <div style={{ background:'var(--surface)', borderRadius:'var(--radius-lg)', padding:28, width:'100%', maxWidth:760, margin:'0 16px 40px', boxShadow:'var(--shadow-lg)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:17, fontWeight:700 }}>{isEdit ? 'Edit Substance' : 'Add Substance'}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
          <div style={{ gridColumn:'1/-1' }}>{field('Substance / Product Name *', 'substance_name')}</div>
          {field('Hazard Classification', 'hazard_classification')}
          {field('GHS Hazard Symbols', 'ghs_symbols')}
          {field('SDS Held?', 'sds_held', { checkbox: true })}
          {field('SDS Date', 'sds_date', { type:'date' })}
          {field('Where Used / Activity', 'where_used', { textarea:true, rows:2 })}
          {field('Storage Location', 'storage_location')}
          {field('Approx. Qty on Site', 'approx_qty_on_site')}
          {field('Who is at Risk', 'who_at_risk', { textarea:true, rows:2 })}
          {field('Route of Exposure', 'exposure_routes', { textarea:true, rows:2 })}
          <div style={{ gridColumn:'1/-1' }}>{field('Engineering Controls', 'control_measures', { textarea:true, rows:3 })}</div>
          <div style={{ gridColumn:'1/-1' }}>{field('PPE Required', 'ppe_required', { textarea:true, rows:2 })}</div>
          <div style={{ gridColumn:'1/-1' }}>{field('First Aid Measures', 'first_aid', { textarea:true, rows:3 })}</div>
          <div style={{ gridColumn:'1/-1' }}>{field('Emergency Action', 'emergency_action', { textarea:true, rows:2 })}</div>
          {field('Responsible Person', 'responsible_person')}
          {field('Date Assessed', 'date_assessed', { type:'date' })}
          {field('Review Due', 'review_due', { type:'date' })}
          {field('Notes', 'notes')}
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20, paddingTop:16, borderTop:'1px solid var(--border)' }}>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary">{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Substance'}</button>
        </div>
      </div>
    </div>
  );
}

function CoshhTab() {
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState(null); // null | 'add' | substance object
  const [expanding, setExpanding] = useState(null);
  const { data, loading, refetch } = useApi(`/coshh?active=${showArchived ? 'false' : 'true'}`);

  const substances = data || [];

  async function archive(id) {
    if (!(await uiConfirm('Archive this substance?'))) return;
    await apiFetch(`/coshh/${id}`, { method: 'DELETE' });
    refetch();
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>COSHH Register</h2>
          <label style={{ fontSize:13, color:'var(--text-muted)', display:'flex', alignItems:'center', gap:6 }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('add')}>+ Add Substance</button>
      </div>

      {loading && <p style={{ color:'var(--text-muted)', fontSize:13 }}>Loading…</p>}

      {!loading && substances.length === 0 && (
        <p style={{ color:'var(--text-muted)', fontSize:13 }}>No substances recorded. Add your first substance above.</p>
      )}

      {!loading && substances.map(sub => {
        const bg = hazardColour(sub.hazard_classification);
        const rev = reviewStatus(sub.review_due);
        const open = expanding === sub.id;
        return (
          <div key={sub.id} style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', marginBottom:8, overflow:'hidden', opacity: sub.is_active ? 1 : 0.55 }}>
            {/* Summary row */}
            <div style={{ display:'grid', gridTemplateColumns:'60px 1fr 180px 120px 130px 80px', alignItems:'center', gap:12, padding:'10px 16px', background: bg, cursor:'pointer' }}
              onClick={() => setExpanding(open ? null : sub.id)}>
              <span style={{ fontWeight:700, fontSize:12, color:'var(--text-muted)' }}>{sub.ref}</span>
              <span style={{ fontWeight:600, fontSize:14 }}>{sub.substance_name}</span>
              <span style={{ fontSize:12, color:'var(--text-muted)' }}>{sub.hazard_classification?.split('\n')[0]}</span>
              <span style={{ fontSize:12, color:'var(--text-muted)' }}>{sub.storage_location || '—'}</span>
              <span>
                {rev && <span style={{ fontSize:11, fontWeight:700, color: rev.colour }}>{rev.label}</span>}
                {!rev && sub.review_due && <span style={{ fontSize:11, color:'var(--text-muted)' }}>Review {new Date(sub.review_due).toLocaleDateString('en-GB')}</span>}
                {!sub.review_due && <span style={{ fontSize:11, color:'var(--text-muted)' }}>No review date</span>}
              </span>
              <span style={{ fontSize:11, color:'var(--text-muted)', textAlign:'right' }}>{open ? '▲' : '▼'}</span>
            </div>

            {/* Expanded detail */}
            {open && (
              <div style={{ padding:'16px 20px', background:'var(--surface)', borderTop:'1px solid var(--border)' }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:16 }}>
                  {[
                    ['Where Used', sub.where_used],
                    ['Who at Risk', sub.who_at_risk],
                    ['Approx. Qty', sub.approx_qty_on_site],
                    ['Route of Exposure', sub.exposure_routes],
                    ['GHS Symbols', sub.ghs_symbols],
                    ['SDS Held', sub.sds_held ? `Yes${sub.sds_date ? ' — ' + new Date(sub.sds_date).toLocaleDateString('en-GB') : ''}` : 'No'],
                  ].map(([label, val]) => val ? (
                    <div key={label}>
                      <div style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', marginBottom:2 }}>{label}</div>
                      <div style={{ fontSize:13 }}>{val}</div>
                    </div>
                  ) : null)}
                </div>
                {[
                  ['Engineering Controls', sub.control_measures],
                  ['PPE Required', sub.ppe_required],
                  ['First Aid', sub.first_aid],
                  ['Emergency Action', sub.emergency_action],
                ].map(([label, val]) => val ? (
                  <div key={label} style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:13, whiteSpace:'pre-wrap' }}>{val}</div>
                  </div>
                ) : null)}
                <div style={{ display:'flex', gap:10, marginTop:16, paddingTop:12, borderTop:'1px solid var(--border)' }}>
                  <button className="btn btn-secondary" onClick={() => setModal(sub)}>Edit</button>
                  {sub.is_active && <button className="btn btn-secondary" style={{ color:'var(--status-critical)' }} onClick={() => archive(sub.id)}>Archive</button>}
                  <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-muted)', alignSelf:'center' }}>
                    Responsible: {sub.responsible_person || '—'}{sub.date_assessed ? ` · Assessed ${new Date(sub.date_assessed).toLocaleDateString('en-GB')}` : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {(modal === 'add' || (modal && modal.id)) && (
        <CoshhModal
          substance={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refetch(); }}
        />
      )}
    </div>
  );
}

// ── Traceability Tests ────────────────────────────────────────────────────────
// Stage 2 of the batches/traceability/recalls build (stage 1 was the Batches
// tab on Own Brand — a test needs a real batch to trace). The recall
// workflow is a separate stage 3 that builds on the same batches.

const TRACE_TEST_TYPES = [
  { value: 'both',     label: 'Both (forward + backward)' },
  { value: 'forward',  label: 'Forward only (batch → outlets)' },
  { value: 'backward', label: 'Backward only (batch → suppliers)' },
];

function PassBadge({ passed }) {
  return passed
    ? <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: '#dcfce7', color: '#166534' }}>Passed</span>
    : <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: '#fee2e2', color: '#991b1b' }}>Failed</span>;
}

function RunTraceTestModal({ onClose, onSaved }) {
  const { data: batchData } = useApi('/own-brand/batches', []);
  const batches = Array.isArray(batchData) ? batchData : (batchData?.batches ?? []);
  const [form, setForm] = useState({ batch_id: '', test_type: 'both', notes: '', next_test_due: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/salsa/traceability-tests/run', {
        method: 'POST',
        body: { ...form, next_test_due: form.next_test_due || undefined },
      });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Run Traceability Test" onClose={onClose} width={520}>
      <form onSubmit={submit}><ErrBanner msg={err} />
        <Field label="Batch *">
          <Select value={form.batch_id} onChange={set('batch_id')} required>
            <option value="">Select batch...</option>
            {batches.map(b => <option key={b.id} value={b.id}>{b.batch_ref} — {b.product_name}</option>)}
          </Select>
        </Field>
        <Field label="Direction">
          <Select value={form.test_type} onChange={set('test_type')}>
            {TRACE_TEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </Field>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} placeholder="Context for this test — e.g. quarterly mock recall exercise" /></Field>
        <Field label="Next test due"><Input type="date" value={form.next_test_due} onChange={set('next_test_due')} /></Field>
        <BtnRow><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={saving || !form.batch_id}>{saving ? 'Running...' : 'Run Test'}</Btn></BtnRow>
      </form>
    </Modal>
  );
}

function TraceTestDetail({ testId, onBack }) {
  const { data, loading } = useApi(`/salsa/traceability-tests/${testId}`, [testId]);
  const test = data;
  const [generating, setGenerating] = useState(false);
  const [reportRef, setReportRef] = useState(null);
  const [reportErr, setReportErr] = useState('');

  async function generateReport() {
    setGenerating(true); setReportErr('');
    try {
      const res = await apiFetch(`/salsa/traceability-tests/${testId}/report`, { method: 'POST' });
      setReportRef(res.data?.document_ref ?? null);
    } catch (err) { setReportErr(err.message); } finally { setGenerating(false); }
  }

  // Documents are served from an authenticated route (Bearer token, no
  // cookie session) — same as the PO print/preview fix — so this fetches
  // with the token and opens the PDF as a blob rather than a plain
  // window.open() straight to the API URL, which would just 401.
  async function downloadReport() {
    try {
      const res = await fetch(`/api/documents/${reportRef}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load report (HTTP ${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to open the report.');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) { alert(err.message); }
  }

  if (loading || !test) return <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: 20 }}>Loading...</p>;

  const fwd = test.forward_trace_result
    ? (typeof test.forward_trace_result === 'string' ? JSON.parse(test.forward_trace_result) : test.forward_trace_result)
    : null;
  const bwd = test.backward_trace_result
    ? (typeof test.backward_trace_result === 'string' ? JSON.parse(test.backward_trace_result) : test.backward_trace_result)
    : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 2 }}>&larr;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{test.test_ref}</span>
            <PassBadge passed={test.passed} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {fmt(test.test_date)} · {test.test_type} trace · Batch {test.batch_ref || '—'} · Conducted by {test.conducted_by_name || '—'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {!reportRef
            ? <Btn size="sm" variant="ghost" onClick={generateReport} disabled={generating}>{generating ? 'Generating...' : 'Generate PDF Report'}</Btn>
            : <Btn size="sm" onClick={downloadReport}>Download Report</Btn>}
        </div>
      </div>
      {reportErr && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{reportErr}</p>}

      {test.notes && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
          {test.notes}
        </div>
      )}

      {fwd && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Forward Trace — Batch → Outlets</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fwd.traceTimeSeconds ?? 0}s · {fwd.outletsCount ?? 0} outlet(s) · {fwd.totalUnitsAllocated ?? 0} units allocated</span>
          </div>
          {!fwd.outlets?.length ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No allocations found for this batch.</p>
          ) : (
            <Table headers={['Outlet', 'Type', 'Units Allocated', 'Units Delivered']}>
              {fwd.outlets.map(o => (
                <TR key={o.outlet_id}>
                  <TD style={{ fontWeight: 600 }}>{o.outlet_name}</TD>
                  <TD>{o.outlet_type}</TD>
                  <TD>{(o.allocations || []).reduce((s, a) => s + (a.units_allocated || 0), 0)}</TD>
                  <TD>{(o.allocations || []).reduce((s, a) => s + (a.units_delivered || 0), 0)}</TD>
                </TR>
              ))}
            </Table>
          )}
        </div>
      )}

      {bwd && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Backward Trace — Batch → Suppliers</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{bwd.traceTimeSeconds ?? 0}s · {bwd.suppliersCount ?? 0} supplier(s)</span>
          </div>
          {bwd.productionRun && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Production run: <strong>{bwd.productionRun.run_ref}</strong></p>
          )}
          {!bwd.inputs?.length ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No production inputs linked — this batch has no production run to trace back through.</p>
          ) : (
            <Table headers={['Ingredient', 'SKU', 'Quantity', 'ABV', 'Lot / PO', 'Supplier']}>
              {bwd.inputs.map(i => {
                const item = (bwd.stockItems || []).find(s => s.stock_item_id === i.stock_item_id);
                const pol = item?.purchaseOrderLines?.[0];
                return (
                  <TR key={i.id}>
                    <TD style={{ fontWeight: 600 }}>{i.stock_item_name}</TD>
                    <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{i.sku}</TD>
                    <TD>{i.quantity}</TD>
                    <TD>{i.abv != null ? `${fmtAbv(i.abv)}%` : '—'}</TD>
                    <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{pol ? `${pol.lot_number || '—'} / ${pol.po_ref || '—'}` : '—'}</TD>
                    <TD>{pol?.supplier_name || '—'}</TD>
                  </TR>
                );
              })}
            </Table>
          )}
        </div>
      )}

      {!fwd && !bwd && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>This test recorded no trace data.</p>}
    </div>
  );
}

function TraceabilityTestsTab() {
  const [showRun, setShowRun] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const { data, loading, refetch } = useApi('/salsa/traceability-tests?limit=100', []);
  const rows = data ?? [];

  if (selectedId) return <TraceTestDetail testId={selectedId} onBack={() => setSelectedId(null)} />;

  return (
    <div>
      {showRun && <RunTraceTestModal onClose={() => setShowRun(false)} onSaved={() => { setShowRun(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>Forward and backward traces recorded against real batch/allocation/purchase-order data — for SALSA's periodic mock-recall requirement.</p>
        <Btn size="sm" onClick={() => setShowRun(true)}>+ Run Test</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No traceability tests recorded yet.</p>
      ) : (
        <Table headers={['Ref', 'Date', 'Type', 'Batch', 'Result', 'Conducted by', 'Next due']}>
          {rows.map(t => (
            <TR key={t.id} onClick={() => setSelectedId(t.id)}>
              <TD style={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.test_ref}</TD>
              <TD>{fmt(t.test_date)}</TD>
              <TD>{t.test_type}</TD>
              <TD style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.batch_ref}</TD>
              <TD><PassBadge passed={t.passed} /></TD>
              <TD>{t.conducted_by_name}</TD>
              <TD>{fmt(t.next_test_due)}</TD>
            </TR>
          ))}
        </Table>
      )}
    </div>
  );
}

const TABS = [
  { key: 'overview',          label: 'Overview' },
  { key: 'traceability',      label: 'Traceability Tests' },
  { key: 'non-conformances',  label: 'Non-Conformances' },
  { key: 'complaints',        label: 'Complaints' },
  { key: 'supplier-approvals',label: 'Supplier Approvals' },
  { key: 'approved-materials',label: 'Approved Materials' },
  { key: 'training',          label: 'Training' },
  { key: 'allergens',         label: 'Allergens' },
  { key: 'haccp',             label: 'HACCP' },
  { key: 'food-fraud',        label: 'Food Fraud' },
  { key: 'maintenance',       label: 'Maintenance' },
  { key: 'coshh',             label: 'COSHH' },
];

export default function Salsa() {
  const [tab, setTab] = useState('overview');
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>SALSA Compliance</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Food safety management — non-conformances, training, supplier approvals and more</p>
      </div>

      {/* Tab bar — scrollable on small screens */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 24, overflowX: 'auto' }}>
        {TABS.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === key ? 700 : 400, color: tab === key ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: tab === key ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap' }}>{label}</button>
        ))}
      </div>

      {tab === 'overview'           && <Overview />}
      {tab === 'traceability'       && <TraceabilityTestsTab />}
      {tab === 'non-conformances'   && <NonConformances />}
      {tab === 'complaints'         && <Complaints />}
      {tab === 'supplier-approvals' && <SupplierApprovals />}
      {tab === 'approved-materials' && <ApprovedMaterials />}
      {tab === 'training'           && <TrainingRecords />}
      {tab === 'allergens'          && <Allergens />}
      {tab === 'haccp'              && <HaccpPlans />}
      {tab === 'food-fraud'         && <FoodFraud />}
      {tab === 'maintenance'        && <Maintenance />}
      {tab === 'coshh'              && <CoshhTab />}
    </div>
  );
}
